import IconFilter from "@mui/icons-material/FilterAlt";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import { GqlFilterOption } from "~/__generated__";
import GaiaFilterHeader from "~/modules/app/components/GaiaFilterHeader";
import useQueryStringGridModels from "~/modules/app/hooks/useQueryStringGridModels";
import { convertParamsToQueryOptions } from "~/modules/app/util/filterAndPaginationUtils";
import { MapPillPanel } from "~/modules/map/components/MapPillPanel";
import { MapEditorFunctionTypeTreeFragment } from "~/modules/mapEditor/components/MapEditorEcologicalFunctionTypeTree/fragments.generated";
import { MapEditorEcologicalFunctionTypeTree } from "~/modules/mapEditor/components/MapEditorEcologicalFunctionTypeTree/MapEditorEcologicalFunctionTypeTree";
import { MapEditorObservationTypeTree } from "~/modules/mapEditor/components/MapEditorObservationTypeTree";
import { MapEditorObservationTypeTreeFragment } from "~/modules/mapEditor/components/MapEditorObservationTypeTree/fragments.generated";
import { MapEditorProtocolTree } from "~/modules/mapEditor/components/MapEditorProtocolTree";
import { MapEditorProtocolTreeFragment } from "~/modules/mapEditor/components/MapEditorProtocolTree/fragments.generated";
import { MapEditorResearchTypeTree } from "~/modules/mapEditor/components/MapEditorResearchTypeTree";
import { MapEditorResearchTypeTreeFragment } from "~/modules/mapEditor/components/MapEditorResearchTypeTree/fragments.generated";
import { ProjectMapEditorFilterVars } from "~/modules/project/hooks/useMapFilters";

type MapEditorFiltersProps = {
    filterOptions: GqlFilterOption[];
    filters: Partial<ProjectMapEditorFilterVars>;
    setFilter: <K extends keyof ProjectMapEditorFilterVars>(key: K, value: ProjectMapEditorFilterVars[K] | undefined) => void;
    functionTypeTreeData?: MapEditorFunctionTypeTreeFragment[];
    observationTypeTreeData?: MapEditorObservationTypeTreeFragment[];
    researchTypeData?: MapEditorResearchTypeTreeFragment[];
    protocolTreeData?: MapEditorProtocolTreeFragment | null;
};

export function MapEditorFilters({
    filterOptions,
    filters,
    setFilter,
    functionTypeTreeData,
    observationTypeTreeData,
    researchTypeData,
    protocolTreeData,
}: MapEditorFiltersProps) {
    // todo: stuck with the filter model until la codie jodie comes to the rescue
    const { filterModel, onFilterModelChange } = useQueryStringGridModels(convertParamsToQueryOptions);
    const { t } = useTranslation("app");

    return (
        <MapPillPanel
            icon={<IconFilter />}
            title={t("gaiaFilterOptions.title")}
            sx={{
                flexGrow: 1,
            }}
            hasDot={Object.keys(filterModel).length > 0 || Object.keys(filters).length > 0}
        >
            <Stack rowGap={1} paddingBlock={2}>
                <GaiaFilterHeader
                    filterOptions={filterOptions}
                    filterModel={filterModel}
                    onFilterModelChange={onFilterModelChange}
                    sx={{ paddingInline: 2 }}
                />
                {functionTypeTreeData !== undefined && (
                    <MapEditorEcologicalFunctionTypeTree
                        onChange={value => setFilter("ecologicalFunctionTypeIds", value.length > 0 ? value : undefined)}
                        functionTypes={functionTypeTreeData}
                        value={filters.ecologicalFunctionTypeIds ?? []}
                    />
                )}
                <MapEditorObservationTypeTree
                    onChange={value => setFilter("observationTypeIds", value.length > 0 ? value : undefined)}
                    observationTypes={observationTypeTreeData}
                    value={filters.observationTypeIds ?? []}
                />
                <MapEditorResearchTypeTree
                    onChange={value => setFilter("researchType", value.length > 0 ? value : undefined)}
                    researchTypes={researchTypeData}
                    value={filters.researchType ?? []}
                />
                <MapEditorProtocolTree
                    onChange={protocolVisitIds =>
                        setFilter("protocolVisitIds", protocolVisitIds.length > 0 ? protocolVisitIds : undefined)
                    }
                    data={protocolTreeData}
                    value={filters.protocolVisitIds ?? []}
                />
            </Stack>
        </MapPillPanel>
    );
}
